// Dialogs.jsx
import {
    useImperativeHandle,
    useRef,
    memo,
    useState,
    useCallback,
    useMemo,
} from 'react'

import { AnimatePresence } from 'framer-motion'
import { DialogContext } from '@shared/hooks/dialogs'

import UserDialog from '@features/users/form/Dialog'
import EstimationForm from '@features/estimations/form/Dialog'
import PreviewEstimation from '@features/estimations/preview/Dialog'

const syncEstimationParam = (id) => {
    const url = new URL(window.location.href)
    if (id) {
        url.searchParams.set('estimation', id)
    } else {
        url.searchParams.delete('estimation')
    }
    window.history.replaceState(window.history.state, '', url)
}

const Dialogs = memo(({ref}) => {
    const [visibility, setVisibility] = useState({})

    useImperativeHandle(ref, () => ({
        show(dialog, props) {
            if (!(dialog in visibility)) {
                setVisibility(prev => ({
                    ...prev,
                    [dialog]: props
                }))
                if (dialog === 'estimation') syncEstimationParam(props?.id)
            }
        },
        showOnly(dialog, props) {
            if (!(dialog in visibility)) {
                setVisibility({[dialog]: props})
                if (dialog === 'estimation') syncEstimationParam(props?.id)
            }
        },
        close(dialog) {
            setVisibility(prev => {
                const { [dialog]: _, ...rest } = prev
                return rest
            })
            if (dialog === 'estimation') syncEstimationParam(null)
        },
        closeAll() {
            setVisibility({})
            syncEstimationParam(null)
        }
    }))

    return (
        <AnimatePresence>
            {'user' in visibility &&
                <UserDialog
                    props={visibility['user']}
                />
            }

            {'estimation' in visibility &&
                <EstimationForm
                    instance={visibility['estimation']}
                />
            }
            
            {'previewEstimation' in visibility &&
                <PreviewEstimation
                    instance={visibility['previewEstimation']}
                />
            }
        </AnimatePresence>
    )
})

export function DialogsProvider({children}) {
    const ref = useRef()

    const show = useCallback((dialog, props) => {
        ref.current?.show(dialog, props)
    }, [])

    const showOnly = useCallback((dialog, props) => {
        ref.current?.showOnly(dialog, props)
    }, [])

    const close = useCallback(dialog => {
        ref.current?.close(dialog)
    }, [])

    const closeAll = useCallback(() => {
        ref.current?.closeAll()
    }, [])

    const value = useMemo(() => ({
        show,
        showOnly,
        close,
        closeAll
    }), [show, showOnly, close, closeAll])

    return (
        <>
            <DialogContext.Provider value={value}>
                {children}
                <Dialogs ref={ref}/>
            </DialogContext.Provider>
        </>
    )
}